import merge from 'lodash/merge';
import { type ThemeOptions, createTheme } from '@mui/material/styles';
import { baseFontSize, typography } from './typography';
import { getLinkPrimaryStyles, getTranslatedLinkStyles, linkStyles } from './links';
import { colorsPalette } from './colorsPalette';
import { getAlertVariants } from './alert';
import { type ChipColor, getChipVariants } from './chip';
import { breakpoints } from './breakpoints';
import { getButtonVariants } from './button';
import { dvh } from './utils';
import { colors } from './colors';

export type { ChipColor, ThemeOptions };
export const unit = 8;
export const MIN_HEIGHT_HEADER = 64;
export const MODAL_Z_INDEX = 1300;
export const SNACKBAR_Z_INDEX = 1400; // notistack forces 1400 so let's keep it consistent

const borderRadius = {
    small: 4,
    medium: 8,
    large: 16,
};

/** @deprecated Legacy. Read `theme.palette.primary` from React context instead. */
export const primary = {
    light: colorsPalette.primarySolid[12],
    main: colorsPalette.primarySolid[100],
    dark: colorsPalette.primaryCustom.dark,
};

const success = {
    light: colors.background['success-subtle'],
    main: colors.background['success-bold'],
    dark: colors.background['success-bold-hover'],
};

const warning = {
    light: colors.background['warning-subtle'],
    main: colors.background['warning-bold'],
    dark: colors.background['warning-bold-hover'],
};

const error = {
    light: colors.background['danger-subtle'],
    main: colors.background['danger-bold'],
    dark: colors.background['danger-bold-hover'],
};

const promotion = {
    light: colorsPalette.purpleSolid[12],
    main: colorsPalette.purpleSolid[100],
    dark: colorsPalette.purpleCustom.dark,
    contrastText: colorsPalette.purpleSolid[100],
};

const pink = {
    main: colorsPalette.pinkSolid[100],
    contrastText: colorsPalette.pinkCustom.dark,
};

const grayscale = {
    light: colorsPalette.blackAlpha[5],
    main: colorsPalette.blackAlpha[20],
    contrastText: colorsPalette.blackSolid[100],
};

const borderWidthDefault = 1;

const buttonBorderWidth = borderWidthDefault;
const buttonPadding = {
    small: { y: 1, x: 1.5 },
    medium: { y: 1.5, x: 2.5 },
    large: { y: 1.75, x: 2.5 },
};
const buttonMargin = {
    small: { y: 1, x: 1 },
    medium: { y: 1.5, x: 1.5 },
    large: { y: 1.75, x: 1.75 },
};

const muiDefaultFadeTransition = '225ms cubic-bezier(0.4, 0, 0.2, 1)';

export const shadowElevationsFactory = {
    level1(colorA = colorsPalette.blackAlpha[10], colorB = colorsPalette.blackAlpha[6]) {
        return `0px 1px 3px 0px ${colorA}, 0px 1px 2px 0px ${colorB}`;
    },
    level2(colorA = colorsPalette.blackAlpha[3], colorB = colorsPalette.blackAlpha[8]) {
        return `0px 4px 8px -2px ${colorA}, 0px 4px 16px -4px ${colorB}`;
    },
    level3(colorA = colorsPalette.blackAlpha[3], colorB = colorsPalette.blackAlpha[8]) {
        return `0px 8px 8px -4px ${colorA}, 0px 12px 24px -4px ${colorB}`;
    },
    level4(colorA = colorsPalette.blackAlpha[8], colorB = colorsPalette.blackAlpha[16]) {
        return `0px 24px 72px -8px ${colorA}, 0px 8px 64px -12px ${colorB}`;
    },
};

const shadows = (() => {
    const muiShadows = createTheme().shadows;

    muiShadows[1] = shadowElevationsFactory.level1();
    muiShadows[2] = shadowElevationsFactory.level2();
    muiShadows[3] = shadowElevationsFactory.level3();
    muiShadows[4] = shadowElevationsFactory.level4();

    return muiShadows;
})();

export const themeOptions: ThemeOptions = {
    breakpoints,
    zIndex: {
        modal: MODAL_Z_INDEX,
        snackbar: SNACKBAR_Z_INDEX,
    },
    colors: colors,
    palette: {
        mode: 'light',
        primary: { main: colors.text.accent },
        // We do not have `secondary` color defined yet in ui-kit, please do not use it.
        secondary: { main: colors.text.accent },
        common: {
            // literal white — common.white is a mode-agnostic primitive, not a swapping token
            white: '#FFFFFF',
        },
        divider: colors.border.strong,
        text: {
            primary: colors.text.default,
            secondary: colors.text.subtle,
        },
        success,
        warning,
        error,
        promotion,
        pink,
        action: {
            disabledBackground: colorsPalette.blackAlpha[20],
            disabled: colorsPalette.blackAlpha[20],
        },
        background: {
            default: colors.background['page-default'],
            paper: colors.background.default,
        },
        grayscale,
    },
    shadows,
    shape: {
        borderRadius: borderRadius.medium,
        borderRadiusSmall: borderRadius.small,
        borderRadiusLarge: borderRadius.large,
    },
    spacing: unit,
    name: 'regular',
    borderWidth: borderWidthDefault,
    typography,
    components: {
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                // hide percy elements
                '@media only percy': {
                    '.hide-in-percy': {
                        visibility: 'hidden',
                    },
                },
                html: {
                    height: '100%',
                    fontSize: baseFontSize,
                },
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: `'Open Sans', sans-serif`,
                    WebkitFontSmoothing: 'antialiased',
                    fontSize: baseFontSize,
                    fontWeight: 400,
                    minHeight: dvh('100dvh'),
                    margin: 0,
                    overflowX: 'hidden',
                    '& > div:first-of-type': {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                },
                svg: {
                    flexShrink: 0,
                    flexGrow: 0,
                },
                '*, *::before, *::after': {
                    boxSizing: 'border-box',
                },
                '*:focus-visible': {
                    outlineColor: theme.palette.primary.main,
                },
                '#root': {
                    flexGrow: 1,
                },
                '.translated-item a': getTranslatedLinkStyles(theme),
            }),
        },
        MuiButton: {
            variants: getButtonVariants(),
            styleOverrides: {
                root: {
                    borderRadius: '999px',
                },
                contained: ({ theme }) => ({
                    '&:disabled': {
                        color: theme.colors.text.inverse,
                    },
                }),
                text: {
                    minWidth: 'auto',
                },
                textPrimary: {
                    '&:hover': {
                        backgroundColor: colorsPalette.primaryAlpha[8],
                        color: colorsPalette.primaryCustom.dark,
                    },
                    '&:active': {
                        color: colorsPalette.primaryCustom.dark,
                    },
                },
                sizeSmall: ({ ownerState, theme }) => ({
                    ...typography.body3,

                    ...((ownerState.variant === 'contained' || ownerState.variant === 'text') && {
                        padding: theme.spacing(buttonPadding.small.y, buttonPadding.small.x),
                    }),

                    ...(ownerState.variant === 'outlined' && {
                        padding: theme.spacing(
                            buttonPadding.small.y - buttonBorderWidth / unit,
                            buttonPadding.small.x - buttonBorderWidth / unit,
                        ),
                    }),
                }),
                sizeMedium: ({ ownerState, theme }) => ({
                    fontSize: theme.typography.pxToRem(16),
                    lineHeight: 1.5,

                    ...((ownerState.variant === 'contained' || ownerState.variant === 'text') && {
                        padding: theme.spacing(buttonPadding.medium.y, buttonPadding.medium.x),
                    }),

                    ...(ownerState.variant === 'outlined' && {
                        padding: theme.spacing(
                            buttonPadding.medium.y - buttonBorderWidth / unit,
                            buttonPadding.medium.x - buttonBorderWidth / unit,
                        ),
                    }),
                }),
                sizeLarge: ({ ownerState, theme }) => ({
                    ...typography.subtitle1,

                    ...((ownerState.variant === 'contained' || ownerState.variant === 'text') && {
                        padding: theme.spacing(buttonPadding.large.y, buttonPadding.large.x),
                    }),

                    ...(ownerState.variant === 'outlined' && {
                        padding: theme.spacing(
                            buttonPadding.large.y - buttonBorderWidth / unit,
                            buttonPadding.large.x - buttonBorderWidth / unit,
                        ),
                    }),
                }),
                startIcon: {
                    transition: `opacity ${muiDefaultFadeTransition}`,
                },
                endIcon: {
                    transition: `opacity ${muiDefaultFadeTransition}`,
                },
                iconSizeSmall: ({ theme }) => ({
                    '& > *:nth-of-type(1)': {
                        fontSize: theme.typography.pxToRem(16),
                    },
                }),
                iconSizeMedium: ({ theme }) => ({
                    '& > *:nth-of-type(1)': {
                        fontSize: theme.typography.pxToRem(24),
                    },
                }),
                iconSizeLarge: ({ theme }) => ({
                    '& > *:nth-of-type(1)': {
                        fontSize: theme.typography.pxToRem(24),
                    },
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    /**
                     * ⚠️ Do NOT override border color on any button state (hover, selected, focus, etc.)
                     *
                     * MUI ToggleButtonGroup (and other grouped-button layouts) rely on adjacent buttons'
                     * borders to render the visual dividers between buttons.
                     *
                     * Changing the border color in any state can cause the visible divider (coming from
                     * the neighboring button's border) to mismatch your own border.
                     *
                     * If you need visual state differentiation, use background color instead.
                     */
                    borderColor: theme.colors.border['interactive-subtle'],
                    //
                    backgroundColor: theme.palette.common.white,
                    fontWeight: 400,
                    '&.Mui-selected': {
                        backgroundColor: theme.colors.background['accent-subtle'],
                        color: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.colors.background['accent-subtle-hover'],
                        },
                        '&.Mui-disabled': {
                            backgroundColor: theme.colors.background['interactive-subtle-disabled'],
                            color: theme.colors.text['accent-disabled'],
                        },
                    },
                    '&.Mui-disabled': {
                        borderColor: theme.colors.border['interactive-subtle-disabled'],
                        color: theme.colors.text['interactive-disabled'],
                    },
                }),
                sizeSmall: ({ theme }) => ({
                    paddingTop: theme.spacing(buttonPadding.small.y - buttonBorderWidth / unit),
                    paddingBottom: theme.spacing(buttonPadding.small.y - buttonBorderWidth / unit),
                }),
                sizeMedium: ({ theme }) => ({
                    paddingTop: theme.spacing(buttonPadding.medium.y - buttonBorderWidth / unit),
                    paddingBottom: theme.spacing(buttonPadding.medium.y - buttonBorderWidth / unit),
                    fontSize: theme.typography.pxToRem(16),
                    lineHeight: 1.5,
                }),
                sizeLarge: ({ theme }) => ({
                    ...typography.subtitle1,
                    fontWeight: 400,
                    paddingTop: theme.spacing(buttonPadding.large.y - buttonBorderWidth / unit),
                    paddingBottom: theme.spacing(buttonPadding.large.y - buttonBorderWidth / unit),
                }),
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                grouped: {
                    // Prevent background from covering adjacent button borders in a group.
                    backgroundClip: 'padding-box',
                },
            },
            variants: [
                {
                    props: { size: 'smallSeparated' },
                    style: ({ theme }) => ({
                        marginLeft: theme.spacing(-buttonMargin.small.x),
                        marginBottom: theme.spacing(-buttonMargin.small.y),

                        '&.MuiToggleButtonGroup-fullWidth': {
                            width: `calc(100% + ${unit * buttonMargin.small.x}px)`,
                        },
                        '&:not(.MuiToggleButtonGroup-fullWidth)': {
                            flexWrap: 'wrap',
                        },
                        '& .MuiToggleButtonGroup-grouped': {
                            // prettier-ignore
                            paddingTop: theme.spacing(buttonPadding.small.y - buttonBorderWidth / unit),
                            // prettier-ignore
                            paddingBottom: theme.spacing(buttonPadding.small.y - buttonBorderWidth / unit),
                        },
                        [`& .MuiToggleButtonGroup-grouped,
                          & .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-middleButton,
                          & .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-lastButton`]: {
                            marginLeft: `${theme.spacing(buttonMargin.small.x)}`,
                            marginBottom: `${theme.spacing(buttonMargin.small.y)}`,
                            borderRadius: borderRadius.small,
                            borderLeft: `1px solid ${theme.colors.border['interactive-subtle']}`,
                            borderTop: `1px solid ${theme.colors.border['interactive-subtle']}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-selected': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.palette.action.disabled,
                            },
                        },
                        '& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected':
                            {
                                borderLeft: `1px solid ${theme.palette.primary.main}`,
                                borderTop: `1px solid ${theme.palette.primary.main}`,
                                marginLeft: theme.spacing(buttonMargin.small.x),
                            },
                    }),
                },
                {
                    props: { size: 'mediumSeparated' },
                    style: ({ theme }) => ({
                        marginLeft: theme.spacing(-buttonMargin.medium.x),
                        marginBottom: theme.spacing(-buttonMargin.medium.y),

                        '&.MuiToggleButtonGroup-fullWidth': {
                            width: `calc(100% + ${unit * buttonMargin.medium.x}px)`,
                        },
                        '&:not(.MuiToggleButtonGroup-fullWidth)': {
                            flexWrap: 'wrap',
                        },
                        '& .MuiToggleButtonGroup-grouped': {
                            // prettier-ignore
                            paddingTop: theme.spacing(buttonPadding.medium.y - buttonBorderWidth / unit),
                            // prettier-ignore
                            paddingBottom: theme.spacing(buttonPadding.medium.y - buttonBorderWidth / unit),
                            fontSize: theme.typography.pxToRem(16),
                            lineHeight: 1.5,
                        },
                        [`& .MuiToggleButtonGroup-grouped,
                          & .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-middleButton,
                          & .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-lastButton`]: {
                            marginLeft: `${theme.spacing(buttonMargin.medium.x)}`,
                            marginBottom: `${theme.spacing(buttonMargin.medium.y)}`,
                            borderRadius: borderRadius.small,
                            borderLeft: `1px solid ${theme.colors.border['interactive-subtle']}`,
                            borderTop: `1px solid ${theme.colors.border['interactive-subtle']}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-selected': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.palette.action.disabled,
                            },
                        },
                        '& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected':
                            {
                                borderLeft: `1px solid ${theme.palette.primary.main}`,
                                borderTop: `1px solid ${theme.palette.primary.main}`,
                                marginLeft: theme.spacing(buttonMargin.medium.x),
                            },
                    }),
                },
            ],
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: unit * 1.5,
                },
            },
            variants: [
                {
                    props: { color: 'default' },
                    style: ({ theme }) => ({
                        color: theme.palette.text.primary,
                    }),
                },
            ],
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    ...linkStyles.root,
                    textDecorationColor: 'currentcolor',
                },
            },
            variants: [
                {
                    props: { color: 'primary' },
                    style: ({ theme }) => {
                        const primaryStyles = getLinkPrimaryStyles(theme);

                        return {
                            ...primaryStyles.root,
                            ':hover': {
                                ...primaryStyles.hover,
                            },
                        };
                    },
                },
            ],
        },
        MuiAlert: {
            variants: getAlertVariants(),
            styleOverrides: {
                root: ({ theme }) => ({
                    border: '1px solid',
                    padding: theme.spacing(2),
                }),
                message: {
                    padding: '2px 0 0',
                },
                icon: {
                    opacity: 1,
                    fontSize: '1.5rem',
                    padding: 0,
                    marginRight: unit * 2,
                },
                action: ({ theme }) => ({
                    padding: `${unit * 0.5}px 0 0 ${unit * 2}px`,
                    margin: `0 0 0 auto`,
                    '& .MuiIconButton-root': {
                        color: theme.palette.text.secondary,
                        padding: unit,
                        margin: -unit,
                        ':hover': {
                            backgroundColor: colorsPalette.blackAlpha[4],
                        },
                        ':focus': {
                            backgroundColor: colorsPalette.blackAlpha[8],
                        },
                    },
                }),
            },
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    ...typography.subtitle1,
                    marginTop: '-1px',
                },
            },
        },
        MuiChip: {
            variants: getChipVariants(),
            styleOverrides: {
                root: {
                    ...typography.body3,
                    borderRadius: borderRadius.small,
                },
                sizeSmall: {
                    paddingTop: unit / 2,
                    paddingBottom: unit / 2,
                },
                icon: {
                    marginLeft: unit,
                    color: 'inherit',
                    fontSize: 16,
                    width: '1em',
                    height: '1em',
                },
                avatar: ({ theme }) => ({
                    color: theme.colors.text.inverse,
                }),
                label: {
                    color: 'inherit',
                },
                deleteIcon: {
                    marginRight: unit,
                    color: 'inherit',
                    '&:hover': {
                        color: 'inherit',
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                popupIndicatorOpen: {
                    transform: 'none',
                },
                endAdornment: {
                    top: 'unset',
                    lineHeight: 1,
                },
                paper: {
                    boxShadow: shadows[2],
                    border: 0,
                },
                listbox: {
                    paddingTop: unit * 2,
                    paddingBottom: unit * 2,
                },
                option: {
                    '&.MuiAutocomplete-option[role="option"]': {
                        minHeight: unit * 6,
                    },
                    '&.MuiAutocomplete-option[aria-selected="true"], &.MuiAutocomplete-option[aria-selected="true"].Mui-focused':
                        {
                            background: colorsPalette.blackAlpha[8],
                        },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.medium,
                },
                select: {
                    padding: unit * 2,
                    minHeight: unit * 2.5,
                    height: unit * 2.5,
                    lineHeight: `${unit * 2.5}px`,
                },
                iconOutlined: {
                    fontSize: unit * 2,
                    right: unit * 1.25,
                    top: `calc(50% - ${unit}px)`,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&[role="option"]': {
                        minHeight: unit * 6,
                    },
                    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
                        background: colorsPalette.blackAlpha[8],
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: MIN_HEIGHT_HEADER,
                    textTransform: 'inherit',
                    padding: unit,
                    ...typography.subtitle1,
                    [breakpoints.up('sm')]: {
                        minWidth: 'initial',
                    },
                },
                textColorInherit: ({ theme }) => ({
                    opacity: 1,
                    '&:not(.Mui-selected)': {
                        color: theme.palette.text.secondary,
                    },
                    '&.Mui-selected': {
                        color: theme.palette.text.primary,
                    },
                    '&:not(.Mui-selected):hover, &:not(.Mui-selected):active, &:not(.Mui-selected):focus':
                        {
                            color: colorsPalette.blackCustom.dark,
                        },
                }),
            },
        },
        MuiTableCell: {
            defaultProps: {
                align: 'left',
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    ...theme.typography.body2,
                    fontVariantNumeric: 'tabular-nums',
                }),
                head: ({ theme }) => ({
                    ...theme.typography.subtitle2,
                    color: theme.palette.text.secondary,
                }),
                sizeMedium: ({ theme, ownerState }) => ({
                    ...(ownerState.padding === 'normal' && {
                        padding: theme.spacing(2, 1),

                        ...(ownerState.variant === 'head' && {
                            paddingBottom: theme.spacing(1),
                        }),
                    }),
                }),
                sizeSmall: ({ theme, ownerState }) => ({
                    ...(ownerState.padding === 'normal' && {
                        padding: theme.spacing(1, 0.5),

                        ...(ownerState.variant === 'head' && {
                            paddingBottom: theme.spacing(1),
                        }),
                    }),
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.common.white,

                    // prettier-ignore
                    '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.colors.border['interactive-subtle-hover'],
                    },

                    '&.Mui-disabled': {
                        backgroundColor: theme.colors.background['interactive-subtle-disabled'],
                        color: theme.colors.text['accent-disabled'],
                    },
                }),
                notchedOutline: ({ theme }) => ({
                    borderColor: theme.colors.border['interactive-subtle'],
                }),
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: borderRadius.medium,
                    },
                    input: {
                        height: 19,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: borderRadius.large,
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                colorPrimary: ({ theme }) => ({
                    color: theme.colors.border['progress-bold'],
                }),
                colorSecondary: ({ theme }) => ({
                    color: theme.colors.border['progress-bold'],
                }),
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                colorPrimary: ({ theme }) => ({
                    backgroundColor: theme.colors.background.stronger,
                }),
                barColorPrimary: ({ theme }) => ({
                    backgroundColor: theme.colors.border['progress-bold'],
                }),
            },
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    zIndex: MODAL_Z_INDEX,
                },
                paper: {
                    minWidth: 280,
                    borderRadius: 0,
                    justifyContent: 'space-between',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.primary,
                    padding: unit * 1.5,
                }),
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: 'auto',
                    color: theme.palette.text.primary,
                    padding: unit * 1.3125,
                }),
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    marginRight: 2,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '1rem',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                gutters: {
                    [breakpoints.only('sm')]: {
                        padding: `0 ${2 * unit}px`,
                    },
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    height: 4,
                    minWidth: 140,
                },
                valueLabel: {
                    top: -22,
                    fontWeight: 600,
                    '& *': {
                        background: 'transparent',
                        color: 'currentColor',
                    },
                },
                rail: {
                    height: 2,
                    backgroundColor: colorsPalette.blackAlpha[20],
                },
                track: {
                    height: 4,
                },
                thumb: {
                    width: 20,
                    height: 20,
                    marginTop: 2,
                    ':before, :after': {
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                typography: 'h2',
            },
        },
        MuiDialogActions: {
            defaultProps: {
                disableSpacing: true,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    gap: theme.spacing(3),

                    [theme.breakpoints.down('sm')]: {
                        flexDirection: 'column-reverse',
                        alignItems: 'stretch',
                        gap: theme.spacing(2),
                    },
                }),
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    boxShadow: shadows[2],
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-selected:hover, &.Mui-selected': {
                        color: theme.palette.primary.main,
                        backgroundColor: colorsPalette.blackAlpha[8],
                    },
                }),
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: colorsPalette.blackAlpha[3],
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontWeight: 600,
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                fontSizeSmall: {
                    fontSize: '1rem', // 16px
                },
                fontSizeMedium: {
                    fontSize: '1.5rem', // 24px
                },
                fontSizeLarge: {
                    fontSize: '2rem', // 32px
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: ({ theme }) => ({
                    background: theme.palette.primary.main,
                    borderRadius: borderRadius.small,
                }),
            },
        },
        MuiSkeleton: {
            variants: [
                {
                    props: { variant: 'rounded' },
                    style: { borderRadius: borderRadius.small },
                },
            ],
            styleOverrides: {
                root: {
                    backgroundColor: '#0000000a',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    position: 'relative',
                    ...typography.overline,
                    textTransform: 'none',
                    padding: '10px 15px',
                    background: colorsPalette.blackSolid[100],
                },
                arrow: {
                    color: colorsPalette.blackSolid[100],
                },
                tooltipPlacementLeft: {
                    margin: '0 8px',
                },
                tooltipPlacementRight: {
                    margin: '0 8px',
                },
                tooltipPlacementTop: {
                    margin: '8px 0',
                },
                tooltipPlacementBottom: {
                    margin: '8px 0',
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(36, 31, 41, 0.5)',
                },
                invisible: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                }),
            },
        },
    },
    transitions: {
        duration: {
            enteringScreen: 300,
        },
    },
};

const invertedPrimary = {
    // contained: bg | outlined/text: text + border color
    main: colors.text.inverse,
    // contained hover: bg
    dark: colors.text['interactive-inverse-hover'],
    // contained: text color (outlined/text ignore contrastText)
    contrastText: colors.text['accent-bold-hover'],
};

const themeInvertedOptions = merge({}, themeOptions, {
    palette: {
        mode: 'dark',
        primary: invertedPrimary,
        secondary: invertedPrimary,
        action: {
            // all buttons disabled: text color
            disabled: colors.text['interactive-inverse-disabled'],
            // contained disabled: bg
            disabledBackground: colors.background['interactive-subtle-disabled'],
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                contained: {
                    '&:disabled': {
                        color: colors.text['accent-inverse-disabled'],
                    },
                },
                outlinedPrimary: {
                    borderColor: colors.border.inverse,
                },
                textPrimary: {
                    color: colors.text.inverse,
                    '&:hover': {
                        backgroundColor: colors.background['interactive-bold'],
                        color: colors.text['interactive-inverse-hover'],
                    },
                    '&:active': {
                        color: colors.text['interactive-inverse-pressed'],
                    },
                },
            },
        },
    },
});

export const theme = createTheme(themeOptions);
export const themeInverted = createTheme(themeInvertedOptions);
